import { TkitUtils as Utils } from '@tkit/types';
import createModel, { Tction } from '@tkit/model';
import uniqBy from 'lodash/uniqBy';
import produce from 'immer';
import { message } from 'antd';
import { IRootState, ListPane } from '@src/types';
import { APIS, SimpleUserInfo } from '@src/services';

import { PAGE_SIZE, LIST_INITIAL, NETWORK_ERROR_TIP } from '@src/utils/constant/common';
import {
  ARTICLES_NAV_KEY_ARTICLE,
  ARTICLES_NAV_KEY_QUESTION,
  ARTICLES_NAV_KEY_ANSWER
} from '@src/utils/constant/articles';

export interface ArticlesNav {
  draft: ListPane<any>;
  post: ListPane<any>;
}

export interface ArticlesModelState {
  article: ArticlesNav;
  question: ArticlesNav;
  answer: ArticlesNav;

  isDeleting: boolean;
}

export const articlesModelState: ArticlesModelState = {
  article: {
    draft: LIST_INITIAL,
    post: LIST_INITIAL
  },
  question: {
    draft: LIST_INITIAL,
    post: LIST_INITIAL
  },
  answer: {
    draft: LIST_INITIAL,
    post: LIST_INITIAL
  },
  isDeleting: false
};

const model = createModel({
  state: articlesModelState,
  namespace: 'articlesModel',
  reducers: {
    resetUserArticlesTabData: (state, action: Tction<{ navKey: string }>) => {
      const { navKey } = action.payload;

      return produce(state, draftState => {
        draftState[navKey].draft = {
          ...LIST_INITIAL
        };
      });
    },

    updateUserArticlesTabLoading: (state, action: Tction<{ navKey: string; loading: boolean }>) => {
      const { navKey, loading } = action.payload;

      return produce(state, draftState => {
        draftState[navKey].draft.loading = loading;
      });
    },
    updateUserArticlesTabHasLoaded: (
      state,
      action: Tction<{ navKey: string; hasLoaded: boolean }>
    ) => {
      const { navKey, hasLoaded } = action.payload;

      return produce(state, draftState => {
        draftState[navKey].draft.hasLoaded = hasLoaded;
      });
    },
    updateUserArticlesTabIsEnded: (state, action: Tction<{ navKey: string; isEnded: boolean }>) => {
      const { navKey, isEnded } = action.payload;

      return produce(state, draftState => {
        draftState[navKey].draft.isEnded = isEnded;
      });
    },
    updateUserArticlesTabList: (
      state,
      action: Tction<{
        navKey: string;
        list: any[];
        pageNum?: number;
        total?: number;
      }>
    ) => {
      const { navKey, list, pageNum, total } = action.payload;

      return produce(state, draftState => {
        const appendData = list.map((item: any) => {
          if (navKey === ARTICLES_NAV_KEY_QUESTION) {
            item.id = item.questionId;
          }

          item.status = item.status || item.articleStatus;

          return item;
        });

        draftState[navKey].draft.list = uniqBy([...appendData], 'id');
        draftState[navKey].draft.pageNum = pageNum;
        draftState[navKey].draft.total = total;
      });
    },
    /** 更新页号 */
    updateArticlesListPage: (
      state,
      action: Tction<{
        navKey: string;
        pageNum: number;
      }>
    ) => {
      const { navKey, pageNum } = action.payload;
      return produce(state, draftState => {
        draftState[navKey].draft.pageNum = pageNum;
      });
    },
    updateUserArticlesIsDeleting: (state, action: Tction<{ isDeleting: boolean }>) => {
      return produce(state, draftState => {
        draftState.isDeleting = action.payload.isDeleting;
      });
    },
    removeUserArticlesDelete: (state, action: Tction<{ navKey: string; index: number }>) => {
      const { navKey, index } = action.payload;

      return produce(state, draftState => {
        draftState[navKey].draft.total = draftState[navKey].draft.total - 1;
        draftState[navKey].draft.list.splice(index, 1);
      });
    }
  },
  effects: {
    doGetUserArticlesTab: [
      function*(
        { tCall, tPut, select },
        action: Tction<{ navKey: string; activeTabKey: string }>
      ): Iterator<{}, any, any> {
        const { navKey, activeTabKey } = action.payload;

        const info = yield select(
          (state: IRootState): ListPane<any> => state.articles[navKey].draft
        );

        yield tPut(model.actions.updateUserArticlesTabLoading, {
          navKey,
          loading: true
        });

        let res;

        if (navKey === ARTICLES_NAV_KEY_ARTICLE) {
          // 获取文章列表
          const userInfo = yield select(
            (state: IRootState): SimpleUserInfo | undefined => state.common.userInfo
          );

          res = yield tCall(APIS.UserApi.userArticleNewUsingGet, {
            type: 'DRAFT',
            targetUser: userInfo.username,
            pageNum: info.pageNum + 1,
            pageSize: PAGE_SIZE
          });
        } else {
          // 获取问题列表
          res = yield tCall(APIS.AnswerApi.answerDraftUsingGet, {
            type: +activeTabKey,
            pageNum: info.pageNum + 1,
            pageSize: PAGE_SIZE
          });
        }

        yield tPut(model.actions.updateUserArticlesTabHasLoaded, {
          navKey,
          hasLoaded: true
        });
        yield tPut(model.actions.updateUserArticlesTabLoading, {
          navKey,
          loading: false
        });

        const { code, result } = res;

        if (code === 0 && result && Array.isArray(result.list) && result.list.length > 0) {
          const { list, pageNum, total } = result;

          yield tPut(model.actions.updateUserArticlesTabList, {
            navKey,
            list,
            pageNum,
            total
          });

          if (total && pageNum && Math.ceil(total / PAGE_SIZE) === pageNum) {
            yield tPut(model.actions.updateUserArticlesTabIsEnded, {
              navKey,
              isEnded: true
            });
          }
        }
      },
      {
        type: 'takeEvery'
      }
    ],
    /** 删除草稿列表 */
    doDeleteUserArticles: [
      function*(
        { tCall, tPut },
        action: Tction<{
          navKey: string;
          index: number;
          id: number | string;
          status: string;
          callBack?: () => void;
        }>
      ): Iterator<{}, any, any> {
        const { navKey, index, id, status, callBack } = action.payload;

        yield tPut(model.actions.updateUserArticlesIsDeleting, {
          isDeleting: true
        });

        let res;
        switch (navKey) {
          case ARTICLES_NAV_KEY_ARTICLE:
            res = yield tCall(APIS.ArticleApi.articleNewBusinessIdUsingDelete, {
              businessId: id as string,
              type: 1
            });
            break;

          case ARTICLES_NAV_KEY_QUESTION:
            res = yield tCall(APIS.QuestionApi.questionQuestionIdUsingDelete, {
              questionId: id as number,
              type: status
            });

            break;
        }

        if (res.code === 0) {
          yield tPut(model.actions.removeUserArticlesDelete, {
            navKey,
            index
          });
          callBack && callBack();
          message.success('删除成功');
        } else {
          message.error(res.message || NETWORK_ERROR_TIP);
        }

        yield tPut(model.actions.updateUserArticlesIsDeleting, {
          isDeleting: false
        });
      },
      {
        type: 'takeLatest'
      }
    ],
    /** 更改页码 */
    *changeArticlesPage(
      { tPut },
      action: Tction<{ navKey: string; tabKey: string; pageNum: number }>
    ): Iterator<{}> {
      const { navKey, tabKey, pageNum } = action.payload;
      yield tPut(model.actions.doGetUserArticlesTab, { navKey, activeTabKey: tabKey });
      yield tPut(model.actions.updateArticlesListPage, {
        navKey,
        pageNum: pageNum - 1
      });
    }
  }
});

export default model;
